/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UiFramework } from "@itwin/appui-react";
import { IModelApp, IModelConnection, ScreenViewport } from "@itwin/core-frontend";
import { Viewer, ViewerNavigationToolsProvider, ViewerViewportControlOptions } from "@itwin/web-viewer-react";
import { GlobalDisplayApi } from "./GlobalDisplayApi";
import { GlobalDisplayWidgetProvider } from "./GlobalDisplayWidget";
import { mapLayerOptions, tileAdminOptions } from "./common/MapLayerOptions";
import { AuthClient } from "./common/AuthClient";
import { Div, FillCentered } from "@itwin/core-react";
import { ProgressLinear } from "@itwin/itwinui-react";
import { DisplayStyleSettingsProps } from "@itwin/core-common";
import { SmartDeviceDecorator } from "./common/decoraters/SmartDeviceDecorator";

const uiProviders = [
  new GlobalDisplayWidgetProvider(),
  new ViewerNavigationToolsProvider(),
];


const setBingMapTransparency = (viewport: ScreenViewport, transparency: number) => {
  viewport.displayStyle.changeBackgroundMapProps({ transparency });
};

const GlobalDisplayApp = () => {
  const authClient = useMemo(() => new AuthClient(), []);
  const [iModelId, setIModelId] = useState(process.env.IMJS_IMODEL_ID);
  const [iTwinId, setITwinId] = useState(process.env.IMJS_ITWIN_ID);
  const [mapTransparency, setMapTransparency] = useState(0.6);
  const [accessToken, setAccessToken] = useState("");
  const login = useCallback(async () => {
    //move to server side
    await authClient.initialize();
    const token = await authClient.getAccessToken();
    setAccessToken(token);
  }, [authClient]);

  useEffect(() => {
    void login();
  }, [login]);

  useEffect(() => {
    if (accessToken) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("iTwinId")) {
        setITwinId(urlParams.get("iTwinId") as string);
        console.log(urlParams.get("iTwinId") as string)
      } else {
        if (!process.env.IMJS_ITWIN_ID) {
          throw new Error(
            "Please add a valid iTwin ID in the .env file and restart the application or add it to the iTwinId query parameter in the url and refresh the page. See the README for more information."
          );
        }
      }

      if (urlParams.has("iModelId")) {
        setIModelId(urlParams.get("iModelId") as string);
        console.log(urlParams.get("iModelId") as string)
      } else {
        if (!process.env.IMJS_IMODEL_ID) {
          throw new Error(
            "Please add a valid iModel ID in the .env file and restart the application or add it to the iModelId query parameter in the url and refresh the page. See the README for more information."
          );
        }
      }
    }
  }, [accessToken]);

  const onIModelConnected = (_imodel: IModelConnection) => {
 
    IModelApp.viewManager.onViewOpen.addOnce (async (vp: ScreenViewport) => {

      vp.displayStyle.setOSMBuildingDisplay({onOff : true});
      const newMapSettings = vp.backgroundMapSettings.clone({transparency: mapTransparency});
      vp.displayStyle.backgroundMapSettings = newMapSettings;
      IModelApp.viewManager.addDecorator(new SmartDeviceDecorator(vp));
    });
  }
  
  return ( 
    <>
      {accessToken === "" 
        ? 
      (<FillCentered>
        <div className="signin-content">
          <ProgressLinear indeterminate={true} labels={["Signing in..."]} />
        </div>
      </FillCentered>) 
      : 
      (
        <Viewer
          iTwinId={iTwinId ?? ""}
          iModelId={iModelId ?? ""}
          authClient={authClient}
          defaultUiConfig={{
            hideStatusBar: true,
            hideToolSettings: true,
          }}
          onIModelConnected={onIModelConnected}
          uiProviders={uiProviders}
          enablePerformanceMonitors={true}
          mapLayerOptions={mapLayerOptions}
          tileAdmin={tileAdminOptions}
          theme={process.env.THEME ?? "dark"}
        />
      )


      }
    </> 
  )
};

// Define panel size
UiFramework.frontstages.onFrontstageReadyEvent.addListener((event) => {
  const { bottomPanel } = event.frontstageDef;
  bottomPanel && (bottomPanel.size = 310);
});

export default GlobalDisplayApp;
